/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import FormSelect from '../../components/forms/form-select';
import { useAccessibleProjects, projectFieldOptions, appIdFromField, defaultProjectId, SHARED_PROJECT } from '../../hooks/useProjectScope';
import { actionMastersService } from '../../services/action-masters.service';
import { valueSetsService } from '../../services/value-sets.service';
import { extractErrorMessage } from '../../utils/error-handler';

const schema = z.object({
  // Empty means shared; the API honours this only inside the caller's scope.
  // Required: a blank appId is what the API reads as "shared with every
  // project", so an unset field would quietly author a global row.
  appId: z.string().min(1, 'Choose the project this belongs to.'),
  actionCode: z.string().regex(/^[A-Z0-9_]{2,64}$/, 'Uppercase letters, digits, underscore (2–64)'),
  actionType: z.string().min(1, 'Type is required'),
  description: z.string().max(1000).optional().or(z.literal('')),
  effectKey: z.string().optional().or(z.literal('')),
  isMemoRequired: z.boolean().optional(),
  style: z.string().optional().or(z.literal('')),
  // configuration, split into the fields the contract actually names rather
  // than one JSON blob. Which of these apply is decided by actionType.
  url: z.string().optional().or(z.literal('')),
  method: z.string().optional().or(z.literal('')),
  payloadTemplate: z.string().optional().or(z.literal('')),
  confirmText: z.string().optional().or(z.literal('')),
});

/** Configuration keys this form edits. Anything else on a row (e.g. an effect's
 *  own `followUp`) is carried through untouched rather than dropped on save. */
const FORM_CONFIG_KEYS = ['url', 'method', 'payloadTemplate', 'confirmText'];

/**
 * What each action type needs configured. Mirrors REQUIRED_ACTION_CONFIG in
 * `_4s_sdlc/notification_engine/masters_service.py` — the API is the gate, this
 * is so the form asks for the right things rather than offering every field for
 * every type and failing on save.
 *
 * BUTTON and ACKNOWLEDGE need nothing: they record a choice and move the
 * recipient's state, which is what the effect key and the attachment's target
 * state are for.
 */
const CONFIG_FIELDS: Record<string, { required: string[]; optional: string[] }> = {
  BUTTON: { required: [], optional: ['confirmText'] },
  ACKNOWLEDGE: { required: [], optional: [] },
  LINK: { required: ['url'], optional: [] },
  DEEP_LINK: { required: ['url'], optional: [] },
  FORM: { required: ['url'], optional: ['payloadTemplate', 'confirmText'] },
  API_CALL: { required: ['url', 'method'], optional: ['payloadTemplate', 'confirmText'] },
};

/** Deliberately not every verb: an action may create or update, never delete. */
const METHODS = ['GET', 'POST', 'PUT', 'PATCH'].map((m) => ({ value: m, label: m }));
type FormData = z.infer<typeof schema>;

const _vsOpts = (vs: any) => (vs?.items || []).map((i: any) => ({ value: i.code, label: i.label }));
const _json = (v: unknown): string => { try { return v ? JSON.stringify(v, null, 2) : ''; } catch { return ''; } };
const _parseObj = (s?: string): any => { if (!s?.trim()) return {}; try { const p = JSON.parse(s); return typeof p === 'object' && p ? p : {}; } catch { return {}; } };

const ActionMasterForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'action', basePath: '/action-masters' });
  const isEdit = Boolean(id);
  const { projects, canScopeByProject } = useAccessibleProjects();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      appId: defaultProjectId(projects),
      actionCode: '', actionType: '', description: '', effectKey: '',
      isMemoRequired: false, style: '',
      url: '', method: '', payloadTemplate: '', confirmText: '',
    },
    mode: 'onChange',
  });

  const actionType = watch('actionType');
  // Configuration the form does not edit, kept so a save does not erase it.
  const extraConfig = useRef<Record<string, any>>({});
  const applies = CONFIG_FIELDS[actionType] || { required: [], optional: [] };
  const shows = (key: string) => applies.required.includes(key) || applies.optional.includes(key);
  const isRequired = (key: string) => applies.required.includes(key);

  // What this engine can actually run. Empty is the normal answer today, and
  // the field is hidden rather than shown as a box that must be left blank.
  const { data: effectKeys } = useQuery({
    queryKey: ['actionEffects'],
    queryFn: () => actionMastersService.effects(),
    staleTime: 3e5,
  });
  const effectOptions = (effectKeys || []).map((k) => ({ value: k, label: k }));
  // A row already naming one keeps showing it, so an effect registered
  // yesterday and removed today is visible rather than silently dropped.
  const storedEffect = watch('effectKey');
  if (storedEffect && !effectOptions.some((o) => o.value === storedEffect)) {
    effectOptions.push({ value: storedEffect, label: `${storedEffect} (not registered)` });
  }

  const { data: typeVS } = useQuery({ queryKey: ['vs', 'actionType'], queryFn: () => valueSetsService.getByKey('actionType', 'en'), staleTime: 3e5 });

  const { data: actionData, isLoading } = useQuery({
    queryKey: ['actionMaster', id],
    queryFn: () => actionMastersService.getById(id!),
    enabled: isEdit && isReady && !!id,
  });

  useEffect(() => {
    if (actionData && isEdit) {
      const config = ((actionData as any).configuration || {}) as Record<string, any>;
      extraConfig.current = Object.fromEntries(
        Object.entries(config).filter(([k]) => !FORM_CONFIG_KEYS.includes(k)));
      reset({
        appId: actionData.appId || SHARED_PROJECT,
        actionCode: actionData.actionCode || '', actionType: actionData.actionType || '',
        description: actionData.description || '', effectKey: actionData.effectKey || '',
        isMemoRequired: !!actionData.isMemoRequired,
        style: _json(actionData.style),
        url: config.url || '',
        method: config.method || '',
        // Stored as an object; edited as text so a template can be written freely.
        payloadTemplate: typeof config.payloadTemplate === 'string'
          ? config.payloadTemplate : _json(config.payloadTemplate),
        confirmText: config.confirmText || '',
      });
    }
  }, [actionData, isEdit, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEdit && id ? actionMastersService.update(id, data) : actionMastersService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actionMasters'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Action ${isEdit ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Save failed'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => {
    // Only the keys this type uses are sent. Carrying over a url from a type
    // that was switched away would leave a BUTTON quietly holding a link.
    const applies = CONFIG_FIELDS[d.actionType] || { required: [], optional: [] };
    const allowed = new Set([...applies.required, ...applies.optional]);
    const source: Record<string, any> = {
      url: d.url, method: d.method, confirmText: d.confirmText,
      payloadTemplate: d.payloadTemplate ? _parseObj(d.payloadTemplate) : undefined,
    };
    const configuration: Record<string, any> = { ...extraConfig.current };
    Object.entries(source).forEach(([key, v]) => {
      if (!allowed.has(key)) return;
      if (v === undefined || v === '' || v === null) return;
      configuration[key] = v;
    });

    mutation.mutate({
    // Create only: the update schema has no appId.
    ...(isEdit ? {} : { appId: appIdFromField(d.appId) }),
      actionCode: d.actionCode, actionType: d.actionType, description: d.description || null,
      // Sent as null rather than '' so the allowlist check treats "no effect" as
      // absent instead of as an unregistered key.
      effectKey: d.effectKey || null,
      isMemoRequired: !!d.isMemoRequired,
      configuration,
      style: _parseObj(d.style),
    });
  };

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <div className="p-4" data-testid="SCR-ActionMaster-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-bolt"
        title={isEdit ? 'Edit Action' : 'Create Action'}
        onBack={() => navigateToList()}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-5xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {canScopeByProject && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''}
                options={projectFieldOptions(projects, field.value)}
                onChange={field.onChange} disabled={isEdit}
                helperText={isEdit
                  ? 'Set when this was created; the API does not move one afterwards.'
                  : 'Which project owns this. It cannot be moved afterwards.'} />
            )} />
          )}
          <Controller name="actionCode" control={control} render={({ field }) => (
            <Input label="Action code *" {...field} error={errors.actionCode?.message} placeholder="APPROVE" disabled={isEdit} />
          )} />
          <Controller name="actionType" control={control} render={({ field }) => (
            <FormSelect label="Type *" name={field.name} value={field.value || ''} options={_vsOpts(typeVS)} onChange={field.onChange} error={!!errors.actionType} helperText={errors.actionType?.message} />
          )} />
          {/* Only when there is something to choose. A free-text box here was
              refused on save for every value except empty, which is not a field
              so much as a trap. */}
          {effectOptions.length > 0 && (
            <Controller name="effectKey" control={control} render={({ field }) => (
              <FormSelect
                label="Effect" name={field.name} value={field.value || ''}
                options={effectOptions} onChange={field.onChange}
                helperText="What runs when somebody takes this action — a built-in, or one your project registered with @notification_action. Leave unset to only record the response."
              />
            )} />
          )}
          <Controller name="isMemoRequired" control={control} render={({ field }) => (
            <div className="flex items-start gap-2 sm:pt-6">
              <Checkbox inputId="memo" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
              <label htmlFor="memo" className="text-sm text-gray-700 dark:text-gray-300 leading-tight">
                Memo required
                <span className="block text-xs text-gray-400">The action is refused without a note.</span>
              </label>
            </div>
          )} />
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
          )} />
          <Controller name="style" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Style (JSON)" {...field} rows={3} placeholder='{ "severity": "success", "icon": "pi pi-check" }' /></div>
          )} />
        </div>

        {actionType && (
          <div className="ds-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Configuration</label>
              <span className="block text-xs text-gray-400">
                {applies.required.length || applies.optional.length
                  ? `What a ${actionType} action needs to do anything.`
                  : `A ${actionType} action needs no configuration — it records the choice and moves the recipient's state.`}
              </span>
            </div>

            {shows('url') && (
              <Controller name="url" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <Input
                    label={`URL${isRequired('url') ? ' *' : ''}`} {...field}
                    placeholder={actionType === 'DEEP_LINK' ? 'app://cases/{caseId}' : '/cases/{caseId}'}
                  />
                  <small className="text-xs text-gray-400">
                    Placeholders are filled from the notification&apos;s parameters, so
                    {' '}<span className="font-mono">{'{caseId}'}</span> must be one it declares.
                  </small>
                </div>
              )} />
            )}

            {shows('method') && (
              <Controller name="method" control={control} render={({ field }) => (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                    Method{isRequired('method') ? ' *' : ''}
                  </label>
                  <Dropdown
                    value={field.value || null} options={METHODS} optionLabel="label" optionValue="value"
                    onChange={(e) => field.onChange(e.value)} placeholder="Select" className="w-full"
                    data-testid="ActionMasterForm.Method"
                  />
                  <small className="text-xs text-gray-400">An action may create or update, never delete.</small>
                </div>
              )} />
            )}

            {shows('confirmText') && (
              <Controller name="confirmText" control={control} render={({ field }) => (
                <Input label="Confirmation prompt" {...field} placeholder="Approve this escalation?" />
              )} />
            )}

            {shows('payloadTemplate') && (
              <Controller name="payloadTemplate" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <TextArea label="Payload template (JSON)" {...field} rows={4}
                    placeholder={'{ "caseId": "{caseId}", "decision": "approved" }'} />
                  <small className="text-xs text-gray-400">
                    Free-form: whatever the endpoint expects. Placeholders are substituted the same way.
                  </small>
                </div>
              )} />
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => navigateToList()} />
          <Button type="submit" label={mutation.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'} icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
        </div>
      </form>
    </div>
  );
};

export default ActionMasterForm;
